import { matchesFieldQuery } from '../table-core/field-sections';
import { useMemo, useRef, useState } from 'react';
import { Columns3 } from 'lucide-react';
import { ResponsivePopover } from '../ResponsivePopover';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';

/**
 * ColumnsPopover — the "Shown / Available" column picker from the prototype
 * behavioral contract (`docs/flow/custom-views-prototype.html`, `#cPop` /
 * `renderColumns()`): a search box, a "Shown" list (drag to reorder, click a
 * name to jump, ✕ to remove), and an "Available" list (click to add).
 *
 * `visibleColumns` is consumed and emitted as ONE ordered array — add,
 * remove, and reorder all produce a new ordered array via `onChange`. The
 * pinned column (`pinnedKey`, default `'name'` — matches
 * `composeColumns()`'s "Name pinned → front-loaded → remaining" rule in
 * `src/shared/flow/contact-view.ts`) is always forced to the front of the
 * emitted array and rendered without drag handles or a remove button, per
 * this unit's brief (a deliberate departure from the prototype, which drags
 * `Name` too).
 *
 * Standalone primitive: owns its own trigger button + open state (same shape
 * as `FlowTable.tsx`'s `GroupByPicker`) so it can be dropped in without a
 * parent managing anchor/open plumbing. Wiring into `FlowTable` itself is a
 * later unit.
 */

export interface ColumnsPopoverColumn {
  key: string;
  label: string;
  /** Optional catalog group label (e.g. "Account", "Tracker") — matches on search too. */
  group?: string;
  /** Muted example line under the label (`fieldExamples` of the catalog entry) — matches on search too. */
  hint?: string;
}

export interface ColumnsPopoverProps {
  /** Full catalog of columns that can be shown, in catalog order. */
  columns: ColumnsPopoverColumn[];
  /** Ordered list of visible column keys (pinnedKey is normalized to the front). */
  visibleColumns: string[];
  onChange: (next: string[]) => void;
  /** Table scrolls its <th> for this key into view; ColumnsPopover doesn't touch the DOM. */
  onJumpToColumn: (key: string) => void;
  /** Key of the pinned, non-draggable/non-removable column. Default 'name'. */
  pinnedKey?: string;
  /** Override the trigger button's classes (defaults to the FlowTable toolbar style). */
  triggerClassName?: string;
  /**
   * Override the trigger's ACCESSIBLE NAME (its visible text stays
   * "Columns"). Needed only where two column pickers share one page — the
   * Plays subtab stacks the plays table's toolbar over the contact table's
   * (U13/A6), and two identically-named buttons are ambiguous for a screen
   * reader and for every `getByRole('button', { name: 'Columns' })`. Same
   * rationale as the Plays mount's "Play filters" trigger (`plays/playsTableConfig.tsx`).
   */
  triggerAriaLabel?: string;
}

const DEFAULT_TRIGGER_CLASSES =
  'flex items-center gap-1 rounded-md border border-secondary bg-white px-2.5 py-1 text-[11.5px] text-content-secondary hover:bg-bg-secondary dark:border-nightshade-700 dark:bg-nightshade-800 dark:text-nightshade-400 dark:hover:bg-nightshade-700';

function matchesQuery(col: { label: string; group?: string; hint?: string }, q: string): boolean {
  return matchesFieldQuery(col, q);
}

export function ColumnsPopover({
  columns,
  visibleColumns,
  onChange,
  onJumpToColumn,
  pinnedKey = 'name',
  triggerClassName,
  triggerAriaLabel,
}: ColumnsPopoverProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dragFromRef = useRef<number | null>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const byKey = useMemo(() => new Map(columns.map(c => [c.key, c])), [columns]);
  const pinnedDef = byKey.get(pinnedKey);
  // The draggable/removable tail — pinnedKey is always stripped out and
  // re-prepended on every emit, so callers never need to special-case it.
  const rest = useMemo(() => visibleColumns.filter(k => k !== pinnedKey), [visibleColumns, pinnedKey]);
  const visibleSet = useMemo(() => new Set([pinnedKey, ...rest]), [pinnedKey, rest]);

  const q = query.trim().toLowerCase();
  const availableColumns = useMemo(
    () => columns.filter(c => !visibleSet.has(c.key) && matchesQuery(c, q)),
    [columns, visibleSet, q],
  );

  const commit = (nextRest: string[]) => onChange([pinnedKey, ...nextRest]);
  const handleAdd = (key: string) => commit([...rest, key]);
  const handleRemove = (key: string) => commit(rest.filter(k => k !== key));
  const handleJump = (key: string) => {
    setOpen(false);
    onJumpToColumn(key);
  };

  const handleDrop = (toIndex: number) => {
    const from = dragFromRef.current;
    dragFromRef.current = null;
    setDraggingIndex(null);
    if (from === null || from === toIndex) return;
    const next = [...rest];
    const [moved] = next.splice(from, 1);
    next.splice(toIndex, 0, moved);
    commit(next);
  };

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        aria-label={triggerAriaLabel}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className={triggerClassName ?? DEFAULT_TRIGGER_CLASSES}
      >
        <Columns3 className="h-3.5 w-3.5" />
        Columns
      </button>
      <ResponsivePopover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={triggerRef}
        ariaLabel="Columns"
        className="w-[300px]"
      >
        <div className="p-2">
          <input
            {...AUTOFILL_IGNORE_PROPS}
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search columns…"
            aria-label="Search columns"
            className="mb-1.5 w-full rounded-md border border-secondary bg-white px-2 py-1.5 text-[12px] text-content-primary placeholder:text-content-placeholder dark:border-nightshade-700 dark:bg-nightshade-800 dark:text-nightshade-100"
          />
          <div className="max-h-[340px] overflow-y-auto">
            <div className="px-1.5 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-wide text-content-tertiary dark:text-nightshade-500">
              Shown — drag to reorder, click a name to jump
            </div>
            {pinnedDef && (
              <div className="flex items-center gap-1.5 rounded-md px-1.5 py-1">
                <span className="w-3 flex-shrink-0" aria-hidden="true" />
                <button
                  type="button"
                  onClick={() => handleJump(pinnedDef.key)}
                  className="flex-1 truncate text-left text-[12px] text-content-primary hover:underline dark:text-nightshade-100"
                >
                  {pinnedDef.label}
                </button>
              </div>
            )}
            {rest.map((key, idx) => {
              const def = byKey.get(key);
              if (!def || !matchesQuery(def, q)) return null;
              return (
                <div
                  key={key}
                  draggable
                  data-testid={`column-row-${key}`}
                  onDragStart={e => {
                    // Firefox never initiates HTML5 drag without a
                    // `setData` call in `dragstart` (F17). jsdom's
                    // synthetic `dragStart` events don't always carry a
                    // `dataTransfer`, hence the optional chaining.
                    e.dataTransfer?.setData('text/plain', key);
                    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
                    dragFromRef.current = idx;
                    setDraggingIndex(idx);
                  }}
                  onDragEnd={() => {
                    dragFromRef.current = null;
                    setDraggingIndex(null);
                  }}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    e.preventDefault();
                    handleDrop(idx);
                  }}
                  className={`flex cursor-grab items-center gap-1.5 rounded-md px-1.5 py-1 hover:bg-bg-secondary dark:hover:bg-nightshade-700 ${
                    draggingIndex === idx ? 'opacity-40' : ''
                  }`}
                >
                  <span
                    className="flex-shrink-0 text-[11px] leading-none text-content-tertiary dark:text-nightshade-500"
                    aria-hidden="true"
                  >
                    ⣿
                  </span>
                  <button
                    type="button"
                    onClick={() => handleJump(key)}
                    className="flex-1 truncate text-left text-[12px] text-content-primary hover:underline dark:text-nightshade-100"
                  >
                    {def.label}
                  </button>
                  {def.group && (
                    <span className="flex-shrink-0 rounded border border-secondary px-1 text-[10px] text-content-tertiary dark:border-nightshade-700 dark:text-nightshade-500">
                      {def.group}
                    </span>
                  )}
                  <button
                    type="button"
                    aria-label={`Remove ${def.label} column`}
                    onClick={() => handleRemove(key)}
                    className="flex-shrink-0 px-1 text-content-tertiary hover:text-content-danger dark:text-nightshade-500"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
            <div className="px-1.5 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-wide text-content-tertiary dark:text-nightshade-500">
              Available
            </div>
            {availableColumns.length === 0 ? (
              <div className="px-1.5 py-2 text-[11.5px] text-content-tertiary dark:text-nightshade-500">
                No matches.
              </div>
            ) : (
              availableColumns.map(c => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => handleAdd(c.key)}
                  className="flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left hover:bg-bg-secondary dark:hover:bg-nightshade-700"
                >
                  <span className="w-3 flex-shrink-0" aria-hidden="true" />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-[12px] text-content-primary dark:text-nightshade-100">{c.label}</span>
                    {c.hint && (
                      <span data-testid="field-hint" className="truncate text-[11px] text-content-tertiary dark:text-nightshade-500">{c.hint}</span>
                    )}
                  </span>
                  {c.group && (
                    <span className="flex-shrink-0 rounded border border-secondary px-1 text-[10px] text-content-tertiary dark:border-nightshade-700 dark:text-nightshade-500">
                      {c.group}
                    </span>
                  )}
                  <span className="flex-shrink-0 text-[12px] text-content-tertiary dark:text-nightshade-500" aria-hidden="true">
                    ＋
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      </ResponsivePopover>
    </div>
  );
}
